/*
 * -------------------------------------------------------------------------------
 * Imports - External
 * -------------------------------------------------------------------------------
 */
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import PSPDFKit, { Configuration, Instance, ToolbarItem } from 'pspdfkit';

/*
 * -------------------------------------------------------------------------------
 * Imports - External
 * -------------------------------------------------------------------------------
 */
import ErrorContext from '@/context/ErrorContext';
import Loading from '@/components/layout/Loading';
import BctIcon from '@/assets/images/bct-logo-horizontal.png';
import { IWindowManagerMessageOptions } from '@ngt/redact/src/WindowManager';
import AlertContext from '@/context/AlertContext';
import { Box, Button, Typography } from '@mui/material';
import { Popup } from '../common/Popup';
import Center from '../common/Center';
import { grey } from '@mui/material/colors';
import PdfViewerStylesUrl from './PdfViewerStyles.css?url';

/*
 * -------------------------------------------------------------------------------
 * Interfaces
 * -------------------------------------------------------------------------------
 */
interface IPdfViewerProps {
    file: File;
    onConfirm: (file: File) => void;
    onCancel: () => void;
    loading: boolean;
    redactOptions?: IWindowManagerMessageOptions;
    onPdfLoaded?: () => void;
    onConfirmClicked?: () => void;
    onRedactionFinished?: () => void;
}

/*
 * -------------------------------------------------------------------------------
 * Main Component
 * -------------------------------------------------------------------------------
 */
const PdfViewer: FC<IPdfViewerProps> = ({
    file,
    redactOptions,
    onConfirm,
    onCancel,
    loading,
    onPdfLoaded,
    onConfirmClicked,
    onRedactionFinished
}) => {
    
    const { addError } = useContext(ErrorContext);
    const { addAlert } = useContext(AlertContext);

    const [displayPopup, setDisplayPopup] = useState<boolean>(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const instanceRef = useRef<Instance | null>(null);

    const onConfirmClick = useCallback(async () => {
        onConfirmClicked && onConfirmClicked();

        if (instanceRef.current) {
            try {
                const buffer = await instanceRef.current.exportPDF();

                const blob = new Blob([buffer], { type: 'application/pdf' });
                
                const fileName = file.name.replace(/\.[^/.]+$/, ".pdf");

                let newFileName = redactOptions?.newFileName ?? `${fileName}`;

                onConfirm && onConfirm(new File([blob], newFileName));
            } 
            catch (error: any) {
                addError({
                    message: error.message
                });
            }
        }

        onRedactionFinished && onRedactionFinished();
    }, [addError, file, instanceRef.current, onConfirm, redactOptions, onConfirmClicked, onRedactionFinished]);

    // Validation
    useEffect(() => {
        const ext = file.name.match(/\.[^/.]+$/);

        //const wordExts: Array<string> = ['.doc', '.docx'];
        const wordExts: Array<string> = [];

        // If the extension could not be found on the file
        if (!ext) {
            addError({
                message: "Unable to identify file extension"
            });
        }
        // If the file was a word doc
        else if (wordExts.includes(ext[0])) {
            addAlert({
                content: 'Word documents are exported as PDF files. Continue only if this is appropriate.',
                timeout: 10000,
                severity: 'warning',
                dismissable: true,
            });
        }

    }, [])

    // Init PSPDFKit web viewer
    useEffect(() => {
        const container = containerRef.current;

        // If the file and the container are initiated
        if (file && container) {
            var instance: Instance;

            const initialiseViewer = async () => {

                try {
                    // Toolbar icon setup 
                    const selectNode = document.createElement("div");
                    selectNode.innerHTML = `<img src=${BctIcon} width=200 style="padding-top: 8px;" />`;

                    // Initial configuration of the PSPDFKit web viewer
                    const config: Configuration = {
                        // The container where PSPDFKit should be mounted
                        container,

                        // The document to open
                        document: await file.arrayBuffer(),

                        // PSPDFKit license key
                        licenseKey: (window as any).PSPDFKEY,

                        // The URL to the location of the PSPDFKit library assets (located at => ./public/pspdfkit-lib)
                        baseUrl: `${window.location.protocol}//${window.location.host}/`,

                        // Initial toolbar items
                        // Added the redaction text highlight and rectangle abilities
                        toolbarItems: [
                            { type: 'custom', node: selectNode },
                            { type: 'redact-text-highlighter', title: 'Text Redaction', dropdownGroup: 'text'},
                            { type: 'redact-rectangle', title: 'Area Redaction', dropdownGroup: 'area'},
                            { type: "spacer" }                      // Adds space in the center of the toolbar
                        ],

                        // CSS style sheets used to style the PSPDFKit viewer.
                        styleSheets: [
                            PdfViewerStylesUrl
                        ]
                    }

                    PSPDFKit.unload(container);

                    // Load PSPDFKit web viewer
                    instance = await PSPDFKit.load(config);

                    instanceRef.current = instance;

                    // Triggers when the state of the document changes. This is used
                    // to detect when a redaction occurs to the document in order to 
                    // enable the confirm button.
                    instance.addEventListener("document.change", () => {
                        instance.setToolbarItems((items) => {
                            return items.map(item => {
                                if (item.id === 'confirm-redact') {
                                    item.disabled = false
                                }

                                return item;
                            })
                        });
                    });

                    onPdfLoaded && onPdfLoaded();

                    // Add additional config buttons
                    const userManualButton: ToolbarItem = {
                        type: 'custom',
                        id: 'user-manual',
                        icon: '',
                        title: 'Redact Function User Manual',
                        onPress: () => {
                            window.open('get/user-manual', '_blank')?.focus();
                        }
                    }
                    
                    const confirmButton: ToolbarItem = {
                        type: 'custom',
                        id: 'confirm-redact',
                        icon: '',
                        title: 'Confirm',
                        disabled: true,
                        onPress: () => {
                            setDisplayPopup(true);
                        }
                    }

                    const cancelButton: ToolbarItem = {
                        type: 'custom',
                        id: 'cancel-redact',
                        icon: '',
                        title: 'Cancel',
                        onPress: () => {
                            onCancel && onCancel();
                        }
                    }

                    const items = instance.toolbarItems;

                    items.push(userManualButton);
                    items.push(confirmButton);
                    items.push(cancelButton);

                    instance.setToolbarItems(items);
                } 
                catch (error: any) {
                    addError({
                        message: error.message
                    });
                }
            }

            initialiseViewer();

            return () => {
                PSPDFKit && PSPDFKit.unload(container);
            };
        }

    }, []);

    return (
        <>
            <Loading name='pdf-viewer' isLoading={loading} isValidating={false} />
            <Popup open={displayPopup} setOpen={setDisplayPopup} position="center">
                <Box
                    sx={{
                        width: '320px',
                        borderRadius: '8px',
                        border: `1px solid ${grey[400]}`,
                        padding: '15px',
                        backgroundColor: grey[200]
                    }}
                >
                    <Typography mb="15px">Do you want to confirm this redaction?</Typography>
                    <Center ha='start'>
                        <Button 
                            variant='contained' 
                            sx={{ minWidth: '100px' }}
                            onClick={onConfirmClick}
                            disabled={loading}
                        >
                            Confirm
                        </Button>
                        <Center width="100%" ha="end">
                            <Button 
                                variant='outlined' 
                                sx={{ width: '100px' }}
                                onClick={() => setDisplayPopup(false)}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                        </Center>
                    </Center>
                </Box>
            </Popup>
            <div
                ref={containerRef}
                style={{
                    width: '100%',
                    height: '100vh'
                }}
            />
        </>
    )
}

export default PdfViewer;
